import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, Upload, Lock } from "lucide-react";

export default function Dashboard() {
    const { user, loading } = useAuth();
    const [submissions, setSubmissions] = useState([]);
    const [community, setCommunity] = useState([]);
    const [form, setForm] = useState({ title: "", location: "", video_url: "", note: "", display_name: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user) return;
        api.get("/community/mine").then((r) => setSubmissions(r.data)).catch(() => {});
        api.get("/community/submissions").then((r) => setCommunity(r.data)).catch(() => {});
    }, [user]);

    if (loading) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-[var(--sb-text-dim)]">Loading…</div>;
    if (!user) return <Navigate to="/login?next=/dashboard" />;

    const isSubscriber = user.is_subscribed || user.role === "admin";

    const submit = async (e) => {
        e.preventDefault();
        if (!isSubscriber) return;
        setBusy(true);
        try {
            const { data } = await api.post("/community/submissions", form);
            setSubmissions([data, ...submissions]);
            setForm({ title: "", location: "", video_url: "", note: "", display_name: "" });
            toast.success("Submission received. It'll appear on the wall once approved.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20" data-testid="dashboard-page">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                <div className="lg:col-span-8">
                    <div className="overline mb-3">Welcome back</div>
                    <h1 className="font-serif text-5xl lg:text-6xl tracking-tighter leading-none mb-4">{user.name?.split(" ")[0] || "Pilot"}.</h1>
                    <p className="text-[var(--sb-text-dim)] text-lg">{user.email}</p>
                </div>
                <div className="lg:col-span-4">
                    <div className="card p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <Crown size={18} className={isSubscriber ? "text-[var(--sb-brand)]" : "text-[var(--sb-text-muted)]"} />
                            <div className="overline">{isSubscriber ? "Subscriber" : "Free"}</div>
                        </div>
                        <p className="text-[var(--sb-text-dim)] text-sm mb-4">
                            {isSubscriber
                                ? "All behind-the-scenes content unlocked. Thank you."
                                : "Unlock exclusive cuts, RAW LOG downloads and the community wall for £1.89/month."}
                        </p>
                        {!isSubscriber && <Link to="/subscription" className="btn-primary text-sm" style={{padding: "0.55rem 1.1rem"}} data-testid="dashboard-upgrade-btn">Upgrade — £1.89</Link>}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                <section className="lg:col-span-7">
                    <div className="flex items-center gap-3 mb-6">
                        <Upload size={18} className="text-[var(--sb-brand)]" />
                        <h2 className="font-serif text-3xl tracking-tight">Share your footage</h2>
                    </div>
                    {isSubscriber ? (
                        <form onSubmit={submit} className="space-y-6" data-testid="submission-form">
                            <div>
                                <label className="overline block mb-2">Clip title</label>
                                <input className="input-line" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="submission-title" />
                            </div>
                            <div>
                                <label className="overline block mb-2">Location</label>
                                <input className="input-line" required value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} data-testid="submission-location" />
                            </div>
                            <div>
                                <label className="overline block mb-2">YouTube / Vimeo URL</label>
                                <input className="input-line" required value={form.video_url} onChange={(e) => setForm({...form, video_url: e.target.value})} data-testid="submission-url" placeholder="https://youtu.be/..." />
                            </div>
                            <div>
                                <label className="overline block mb-2">Display name on Community Spotlight</label>
                                <input className="input-line" value={form.display_name} onChange={(e) => setForm({...form, display_name: e.target.value})} data-testid="submission-display-name" placeholder={user.name} />
                                <p className="text-xs text-[var(--sb-text-muted)] mt-2">Leave blank to use your account name. Use a first name, initials, or a pen name.</p>
                            </div>
                            <div>
                                <label className="overline block mb-2">The story behind this clip</label>
                                <textarea className="input-line" rows={8} value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} data-testid="submission-note" placeholder="Why does this place matter to you? What was the weather doing? What were you thinking about when you launched the drone?" />
                                <p className="text-xs text-[var(--sb-text-muted)] mt-2">This is the heart of the Community Spotlight — your words go alongside your footage.</p>
                            </div>
                            <button disabled={busy} className="btn-primary" data-testid="submission-submit">{busy ? "…" : "Submit to Community Spotlight"}</button>
                        </form>
                    ) : (
                        <div className="card p-8 text-center">
                            <Lock className="mx-auto text-[var(--sb-text-muted)] mb-4" />
                            <p className="text-[var(--sb-text-dim)] mb-4">The community wall is for subscribers.</p>
                            <Link to="/subscription" className="btn-primary">Subscribe — £1.89</Link>
                        </div>
                    )}

                    {submissions.length > 0 && (
                        <div className="mt-12">
                            <h3 className="font-serif text-xl mb-4">Your submissions</h3>
                            <div className="space-y-3" data-testid="my-submissions">
                                {submissions.map((s) => (
                                    <div key={s.id} className="card p-5 flex flex-wrap justify-between gap-3 items-start">
                                        <div>
                                            <div className="font-serif text-lg">{s.title}</div>
                                            <div className="text-xs text-[var(--sb-text-muted)] uppercase tracking-wider mt-1">{s.location} · {s.approved ? "Live on wall" : "Pending review"}</div>
                                        </div>
                                        <a href={s.video_url} target="_blank" rel="noreferrer" className="text-sm text-[var(--sb-brand)] hover:text-[var(--sb-text)]">Watch ↗</a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </section>

                <aside className="lg:col-span-5">
                    <h2 className="font-serif text-3xl tracking-tight mb-6">Community wall</h2>
                    {community.length === 0 ? (
                        <div className="card p-8 text-[var(--sb-text-dim)] text-sm">
                            Approved subscriber submissions show up here. Be the first.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {community.map((s) => (
                                <div key={s.id} className="card p-5">
                                    <div className="overline mb-1">{s.user_name}</div>
                                    <div className="font-serif text-lg">{s.title}</div>
                                    <p className="text-sm text-[var(--sb-text-dim)] mt-1">{s.location}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}
